import React, { useState } from 'react';
import { Phone, Calendar, Send, Facebook, Linkedin, Instagram } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import axios from 'axios';
import { toast } from 'sonner';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: '',
    company_website: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleServiceChange = (value) => {
    setFormData({
      ...formData,
      service: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post('/contact.php', formData);
      if (response.data && response.data.success) {
        setIsSubmitted(true);
        toast.success("Thank you! We'll be in touch within 24 hours.");
        setFormData({
          name: '',
          email: '',
          phone: '',
          service: '',
          message: '',
          company_website: ''
        });
        setTimeout(() => setIsSubmitted(false), 5000);
      }
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactCards = [
    {
      icon: Phone,
      title: 'Call Us',
      content: '+971 54 274 7863',
      link: 'tel:+971542747863'
    },
    {
      icon: Calendar,
      title: 'Book a Strategy Call',
      content: 'Schedule a free consultation',
      link: 'https://booking.impactechdigital.com',
      external: true
    }
  ];

  const socialLinks = [
    {
      name: 'Facebook',
      icon: Facebook,
      url: 'https://www.facebook.com/profile.php?id=61578070613491'
    },
    {
      name: 'LinkedIn',
      icon: Linkedin,
      url: 'https://www.linkedin.com/company/impactech-digital/'
    },
    {
      name: 'Instagram',
      icon: Instagram,
      url: 'https://www.instagram.com/impactechdigital/'
    }
  ];

  const serviceCategories = [
    'Website Design & Development',
    'Advertising',
    'Content Creation',
    'Mobile App Development',
    'Lead Generation & Automation',
    'CRM Implementation',
    'Zoho Implementation & Consulting',
    'Marketplace Setup',
    'Social Media Marketing',
    'SEO Services',
    'Branding & Design',
    'E-Commerce Solutions',
    'IT & Cyber Security',
    'Other'
  ];

  return (
    <div className="bg-white">
      {/* Page Header */}
      <section className="py-20" style={{ backgroundColor: 'rgba(17, 170, 182, 0.05)', borderBottom: '1px solid rgba(5, 46, 108, 0.12)' }}>
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h1 className="font-['Space_Grotesk'] font-bold text-5xl md:text-6xl text-[#052E6C] mb-6">
            Let's talk growth.
          </h1>
          <p className="font-['Inter'] text-xl leading-relaxed" style={{ color: 'rgba(5, 46, 108, 0.7)' }}>
            Whether you have a specific project in mind or just want to explore possibilities, we're here to help. Reach out and let's start the conversation.
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-12">
            {/* Left Column - Contact Form */}
            <div className="lg:col-span-3">
              <Card className="shadow-lg" style={{ border: '1px solid rgba(5, 46, 108, 0.12)' }}>
                <CardContent className="p-8 md:p-10">
                  {isSubmitted ? (
                    <div className="text-center py-12">
                      <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#11AAB6] to-[#052E6C] flex items-center justify-center mx-auto mb-6">
                        <Send className="text-white" size={32} />
                      </div>
                      <h3 className="font-['Space_Grotesk'] font-bold text-2xl text-[#052E6C] mb-3">
                        Message sent!
                      </h3>
                      <p className="font-['Inter']" style={{ color: 'rgba(5, 46, 108, 0.7)' }}>
                        Thank you for reaching out. We'll get back to you within 24 hours.
                      </p>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-6">
                      {/* Honeypot — hidden from humans, catches spam bots */}
                      <input
                        type="text"
                        name="company_website"
                        value={formData.company_website}
                        onChange={handleChange}
                        tabIndex={-1}
                        autoComplete="off"
                        style={{ position: 'absolute', left: '-9999px', height: 0, width: 0, opacity: 0 }}
                        aria-hidden="true"
                      />
                      <div>
                        <h3 className="font-['Space_Grotesk'] font-bold text-2xl text-[#052E6C] mb-2">
                          Send us a message
                        </h3>
                        <p className="font-['Inter'] text-sm" style={{ color: 'rgba(5, 46, 108, 0.7)' }}>
                          Fill out the form below and we'll respond as soon as possible.
                        </p>
                      </div>

                      <div className="grid md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <label className="font-['Inter'] text-sm font-medium text-[#052E6C]">
                            Full Name *
                          </label>
                          <Input
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            placeholder="John Doe"
                            required
                            className="focus:ring-2 focus:ring-opacity-25"
                            style={{ 
                              border: '1px solid rgba(5, 46, 108, 0.12)',
                              outline: 'none'
                            }}
                            onFocus={(e) => e.currentTarget.style.boxShadow = '0 0 0 3px rgba(17, 170, 182, 0.25)'}
                            onBlur={(e) => e.currentTarget.style.boxShadow = 'none'}
                          />
                        </div>

                        <div className="space-y-2">
                          <label className="font-['Inter'] text-sm font-medium text-[#052E6C]">
                            Email Address *
                          </label>
                          <Input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="john@example.com"
                            required
                            style={{ 
                              border: '1px solid rgba(5, 46, 108, 0.12)',
                              outline: 'none'
                            }}
                            onFocus={(e) => e.currentTarget.style.boxShadow = '0 0 0 3px rgba(17, 170, 182, 0.25)'}
                            onBlur={(e) => e.currentTarget.style.boxShadow = 'none'}
                          />
                        </div>
                      </div>

                      <div className="grid md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <label className="font-['Inter'] text-sm font-medium text-[#052E6C]">
                            Phone Number
                          </label>
                          <Input
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            placeholder="+971 50 123 4567"
                            style={{ 
                              border: '1px solid rgba(5, 46, 108, 0.12)',
                              outline: 'none'
                            }}
                            onFocus={(e) => e.currentTarget.style.boxShadow = '0 0 0 3px rgba(17, 170, 182, 0.25)'}
                            onBlur={(e) => e.currentTarget.style.boxShadow = 'none'}
                          />
                        </div>

                        <div className="space-y-2">
                          <label className="font-['Inter'] text-sm font-medium text-[#052E6C]">
                            What do you need help with? *
                          </label>
                          <Select value={formData.service} onValueChange={handleServiceChange} required>
                            <SelectTrigger 
                              style={{ 
                                border: '1px solid rgba(5, 46, 108, 0.12)',
                                outline: 'none'
                              }}
                            >
                              <SelectValue placeholder="Select a service" />
                            </SelectTrigger>
                            <SelectContent>
                              {serviceCategories.map((service) => (
                                <SelectItem key={service} value={service}>
                                  {service}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <label className="font-['Inter'] text-sm font-medium text-[#052E6C]">
                          Your Message *
                        </label>
                        <Textarea
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          placeholder="Tell us about your project, goals, or any questions you have..."
                          rows={6}
                          required
                          className="resize-none"
                          style={{ 
                            border: '1px solid rgba(5, 46, 108, 0.12)',
                            outline: 'none'
                          }}
                          onFocus={(e) => e.currentTarget.style.boxShadow = '0 0 0 3px rgba(17, 170, 182, 0.25)'}
                          onBlur={(e) => e.currentTarget.style.boxShadow = 'none'}
                        />
                      </div>

                      <Button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full bg-[#11AAB6] hover:bg-[#0E8A94] text-white py-6 text-lg rounded-full font-['Inter'] font-medium shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 group"
                      >
                        {isSubmitting ? (
                          'Sending...'
                        ) : (
                          <>
                            Send Message
                            <Send className="ml-2 inline-block group-hover:translate-x-1 transition-transform" size={20} />
                          </>
                        )}
                      </Button>
                    </form>
                  )}
                </CardContent>
              </Card>
            </div>
            {/* Right Column - Contact Info */}
            <div className="lg:col-span-2 space-y-8">
              <div>
                <h2 className="font-['Space_Grotesk'] font-bold text-3xl text-[#052E6C] mb-6">
                  Get in touch
                </h2>
                <p className="font-['Inter'] leading-relaxed mb-8" style={{ color: 'rgba(5, 46, 108, 0.7)' }}>
                  Have questions? Need a quote? Want to discuss how automation can transform your business? We're just a message away.
                </p>
              </div>

              {/* Contact Cards */}
              <div className="space-y-4">
                {contactCards.map((card, index) => (
                  <Card key={index} className="transition-all duration-300 hover:shadow-md" style={{ border: '1px solid rgba(5, 46, 108, 0.12)' }}>
                    <CardContent className="p-6">
                      {card.link ? (
                        <a 
                          href={card.link} 
                          {...(card.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                          className="flex items-start space-x-4 group"
                        >
                          <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform text-[#11AAB6]" style={{ backgroundColor: 'rgba(17, 170, 182, 0.12)' }}>
                            <card.icon size={20} />
                          </div>
                          <div>
                            <h3 className="font-['Space_Grotesk'] font-semibold text-[#052E6C] mb-1">{card.title}</h3>
                            <p className="font-['Inter'] text-sm" style={{ color: 'rgba(5, 46, 108, 0.7)' }}>{card.content}</p>
                          </div>
                        </a>
                      ) : (
                        <div className="flex items-start space-x-4">
                          <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 text-[#11AAB6]" style={{ backgroundColor: 'rgba(17, 170, 182, 0.12)' }}>
                            <card.icon size={20} />
                          </div>
                          <div>
                            <h3 className="font-['Space_Grotesk'] font-semibold text-[#052E6C] mb-1">{card.title}</h3>
                            <p className="font-['Inter'] text-sm" style={{ color: 'rgba(5, 46, 108, 0.7)' }}>{card.content}</p>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Social Links */}
              <div>
                <h3 className="font-['Space_Grotesk'] font-semibold text-[#052E6C] mb-4">Follow us</h3>
                <div className="flex space-x-3">
                  {socialLinks.map((social) => (
                    <a
                      key={social.name}
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-12 h-12 rounded-full flex items-center justify-center text-[#11AAB6] transition-all duration-300 hover:scale-110"
                      style={{ backgroundColor: 'rgba(17, 170, 182, 0.12)' }}
                      aria-label={social.name}
                    >
                      <social.icon size={20} />
                    </a>
                  ))}
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      <style>{`
        @media (prefers-reduced-motion: reduce) {
          * {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
          }
        }
      `}</style>
    </div>
  );
};

export default Contact;
